import { useCallback, useState } from 'react';
import EmailConfigurationFormComponent from '../components/emailConfiguration-form.component';
import {
  EmailConfigurationCreate,
  EmailConfigurationUpdate,
} from '../types/emailConfiguration.types';
import { useEmailConfigurationCreate } from '../hooks/use-emailConfiguration.hook';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const EmailConfigurationCreateContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const { mutateAsync: createEmailConfiguration, isPending } = useEmailConfigurationCreate();

  const onFormSubmit = useCallback(
    async (data: EmailConfigurationCreate | EmailConfigurationUpdate, resetForm: () => void) => {
      // Reset validation errors on new submission
      setValidationErrors([]);

      // For create operations, we know we'll have all required fields
      const createData = data as EmailConfigurationCreate;
      try {
        const response = await createEmailConfiguration(createData);
        message.success(t('crud.create.success', { entity: t('emailConfiguration.title') }));
        resetForm();
        navigate(`/dashboard/admin/emailConfiguration/${response.data.id}/update`);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.create.error', { entity: t('emailConfiguration.title') })
        );
        message.error(errorMessage);
      }
    },
    [createEmailConfiguration, navigate, t]
  );

  return (
    <>
      <PageTitle title={t('crud.create.title', { entity: t('emailConfiguration.title') })} />
      <HeaderRow
        title={t('crud.create.title', { entity: t('emailConfiguration.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <EmailConfigurationFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(
  EmailConfigurationCreateContainer,
  'emailConfiguration.create'
);
